import { MEDLEVE_ORIGIN } from './constants'

/**
 * Normalizes any path or URL to a site-relative pathname: no host, no query string, no hash,
 * no trailing slash (except the root).
 */
export const toPathname = (pathOrUrl: string): string => {
  const withoutOrigin = pathOrUrl.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/?#]*/i, '')
  const pathname = withoutOrigin
    .split(/[?#]/)[0]
    .replace(/\/{2,}/g, '/')
    .replace(/\/+$/, '')
  if (pathname === '') return '/'
  return pathname.startsWith('/') ? pathname : `/${pathname}`
}

/** Absolute URL on the canonical Medleve origin. Keeps whatever query/hash the path carries. */
export const absoluteUrl = (path: string): string => {
  if (/^https?:\/\//i.test(path)) return path
  return `${MEDLEVE_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`
}

export interface CanonicalOptions {
  /**
   * Query parameters that change the page content and therefore belong in the canonical.
   * Default: none — every parameter (utm_*, gclid, fbclid, ttclid, msclkid, …) is dropped.
   */
  contentParams?: string[]
}

/**
 * Canonical URL (PRD §36): always `https://www.medleve.com` + pathname, never the request host,
 * with campaign/tracking parameters and the hash removed. Accepts `router.asPath`, `req.url` or a full URL.
 *
 * This only computes the `<link rel="canonical">` value. It must never be used to redirect or rewrite
 * the visitor's URL — attribution depends on the landing URL reaching GTM intact.
 */
export const canonicalUrl = (pathOrUrl: string, { contentParams = [] }: CanonicalOptions = {}): string => {
  const base = `${MEDLEVE_ORIGIN}${toPathname(pathOrUrl)}`
  if (!contentParams.length) return base

  const query = pathOrUrl.split('#')[0].split('?')[1]
  if (!query) return base

  const incoming = new URLSearchParams(query)
  const kept = new URLSearchParams()
  ;[...contentParams].sort().forEach((name) => {
    incoming.getAll(name).forEach((value) => kept.append(name, value))
  })
  const serialized = kept.toString()
  return serialized ? `${base}?${serialized}` : base
}
